import React, { useCallback, useMemo, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { FormikHelpers } from "formik";
import { useHistory } from "react-router-dom";

import { translationService } from "../../../services/translation/TranslationService";
import { CurrentSectionStorage } from "../../../services/sewer-object/CurrentSectionStorage";
import { EditDynamicProperties } from "../../../components/data-components/EditDynamicProperties";
import Form from "../../../components/form/Form";
import { DamageInput } from "../../../api-models/request/DamageInput";
import { AddDamageImage, DamageFormValues } from "../components/DataComponents";
import useErrorHandler, {
  ApplicationError,
} from "../../../services/error-handler/ErrorHandlerService";
import { DataSection, SubSection } from "../../../components/data-components/sections/DataSections";
import DisplayDateCreated from "../../../components/data-components/DisplayDateCreated";
import DisplayCreatedBy from "../../../components/data-components/DisplayCreatedBy";
import EditGroupCode from "../components/EditGroupCode";
import { DamageGroupCode } from "../../../services/damage/DamageGroupCode";
import EditMainCode from "../components/EditMainCode";
import { DamageMainCode } from "../../../services/damage/DamageMainCode";
import EditC1Code from "../components/EditC1Code";
import EditC2Code from "../components/EditC2Code";
import { DamageC1Code } from "../../../services/damage/DamageC1Code";
import { DamageC2Code } from "../../../services/damage/DamageC2Code";
import { DamageDetails } from "../../../services/damage/DamageDetails";
import { UploadImageStateModel } from "../../../components/image-upload/ImageUploadModel";
import { CurrentInspectionStorage } from "../../../services/inspection/CurrentInspectionStorage";
import { emptyDamage } from "../../../services/damage/CurrentDamageStorage";
import { DamagePropertiesStorage } from "../../../services/damage/DamagePropertiesStorage";
import { videoPlayerService } from "../../../services/video-player/VideoPlayerService";
import LoadingSpinner from "../../../components/loading-spinner/LoadingSpinner";
import { DamageCodesStorageModel } from "../../../services/damage/DamageCodesStorage";
import { warningLeavePageService } from "../../../services/page/WarningLeavePageService";
import { navigatePath } from "../../../helpers/StringHelpers";
import { FieldsFromBM } from "../../../api-models/response/BluemetricFields";

interface CreateDamageProps {
  currentSectionStorage: CurrentSectionStorage;
  damagePropertiesStorageModel: DamagePropertiesStorage;
  currentInspectionStorage: CurrentInspectionStorage;
}

const CreateDamage = observer((props: CreateDamageProps) => {
  const { currentInspectionStorage, damagePropertiesStorageModel } = props;

  const history = useHistory();

  const { handleErrorInSnackbar } = useErrorHandler();

  const [initialEmptyDamage, setInitialEmptyDamage] = useState(new DamageDetails(emptyDamage));

  const [damageCodesStorage] = useState(DamageCodesStorageModel.create({}));

  const [imageUploadState] = useState(() => UploadImageStateModel.create({}));

  const damageIsLockedWarning = useMemo(() => {
    if (
      !currentInspectionStorage.inspectionIsLockedBySomeoneElse ||
      !currentInspectionStorage.userWithLockOnInspection
    )
      return undefined;

    return {
      key: "CreateDamage.locked-inspection",
      context: { userName: currentInspectionStorage.userWithLockOnInspection.name },
    };
  }, [
    currentInspectionStorage.inspectionIsLockedBySomeoneElse,
    currentInspectionStorage.userWithLockOnInspection,
  ]);

  const createDamageFromMetadata = useCallback(() => {
    const damage: DamageDetails = DamageDetails.create(damagePropertiesStorageModel.damageMetadata);
    damage.setDynamicProperties(damagePropertiesStorageModel.damageMetadata.metadata);
    return damage;
  }, [damagePropertiesStorageModel.damageMetadata]);

  const getDynamicPropertiesInitialValues = useCallback(() => {
    if (damageCodesStorage.damageCharacteristicsProperties)
      return damageCodesStorage.extractFieldsFromBM(
        damageCodesStorage.damageCharacteristicsProperties
      );
  }, [damageCodesStorage]);

  const goToInitialPage = useCallback(
    () => history.push(navigatePath(history.location.pathname, -1)),
    [history]
  );

  const getInitialValues = useCallback(() => {
    return {
      dynamicProperties: getDynamicPropertiesInitialValues(),
      groupCode: "all",
      mainCode: damageCodesStorage.initialMainCode,
      c1Code: damageCodesStorage.initialC1Code,
      c2Code: damageCodesStorage.initialC2Code,
    };
  }, [
    damageCodesStorage.initialC1Code,
    damageCodesStorage.initialC2Code,
    damageCodesStorage.initialMainCode,
    getDynamicPropertiesInitialValues,
  ]);

  const handleSubmit = useCallback(
    async (formValues: DamageFormValues, formActions: FormikHelpers<FieldsFromBM>) => {
      const mainCode = damageCodesStorage.getFieldAsBMField(
        damageCodesStorage.mainCodeFieldIndicator,
        damageCodesStorage.selectedMainCode?.code
      );
      const c1Code = damageCodesStorage.getFieldAsBMField(
        damageCodesStorage.c1CodeFieldIndicator,
        !damageCodesStorage.selectedMainCode?.hasC1Codes
          ? ""
          : damageCodesStorage.selectedC1Code.code
      );
      const c2Code = damageCodesStorage.getFieldAsBMField(
        damageCodesStorage.c2CodeFieldIndicator,
        damageCodesStorage.selectedC2Code.code
      );
      const cleanedDynamicProperties =
        initialEmptyDamage.getDynamicPropertiesWithDecimalsAndDateFormatted(
          formValues.dynamicProperties
        );

      Object.keys(cleanedDynamicProperties).forEach((key) => {
        if (
          damagePropertiesStorageModel.damageDetailsForCodes.disabledFields.find(
            (disabledField) => disabledField === key
          )
        ) {
          cleanedDynamicProperties[key] = null;
        }
      });

      const damageInput: DamageInput = {
        file: imageUploadState.imageForUpload,
        dynamicProperties: { ...cleanedDynamicProperties, ...mainCode, ...c1Code, ...c2Code },
      };

      try {
        await damagePropertiesStorageModel.createDamage(
          currentInspectionStorage.currentInspectionId,
          damageInput
        );
        warningLeavePageService.setIsDirty(false);
        return goToInitialPage();
      } catch (error) {
        const applicationError = error as ApplicationError;
        applicationError.withForm(formValues, formActions);
        applicationError.withErrorMessage("default-error.damage.create");
        handleErrorInSnackbar(applicationError);
      }
    },
    [
      currentInspectionStorage.currentInspectionId,
      damageCodesStorage,
      damagePropertiesStorageModel,
      goToInitialPage,
      handleErrorInSnackbar,
      imageUploadState.imageForUpload,
      initialEmptyDamage,
    ]
  );

  const isLoadingDamageCharacteristics = useCallback(() => {
    return damageCodesStorage.loadingDamageCharacteristics;
  }, [damageCodesStorage.loadingDamageCharacteristics]);

  const getC2Codes = useCallback(() => {
    return damageCodesStorage.c2CodesAreLoading ? [] : damageCodesStorage.c2Codes;
  }, [damageCodesStorage.c2Codes, damageCodesStorage.c2CodesAreLoading]);

  useEffect(() => {
    const setupDamageDataForCreate = async () => {
      try {
        await damagePropertiesStorageModel.getDamageMetadata();
        await damagePropertiesStorageModel.getDamageGroupCodes();
      } catch (error) {
        const applicationError = error as ApplicationError;
        applicationError.withErrorMessage("default-error.damage.create-setup");
        handleErrorInSnackbar(applicationError);
      }

      const emptyDamageCreatedFromMetadata = createDamageFromMetadata();
      damageCodesStorage.initialDataReceived(
        damagePropertiesStorageModel.groupCodes,
        emptyDamageCreatedFromMetadata,
        videoPlayerService.selectedVADetection
      );
      setInitialEmptyDamage(emptyDamageCreatedFromMetadata);
    };
    setupDamageDataForCreate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function getC2Codes() {
      try {
        await damagePropertiesStorageModel.getDamageC2CodesForMainAndC1Code(
          damageCodesStorage.selectedMainCode?.code ?? "",
          damageCodesStorage.selectedC1Code.code
        );
        damageCodesStorage.receivedC2Codes(damagePropertiesStorageModel.c2Codes);
      } catch (error) {
        const applicationError = error as ApplicationError;
        applicationError.withErrorMessage("default-error.damage.c2Codes");
        handleErrorInSnackbar(applicationError);
      }
    }
    if (damageCodesStorage.selectedC1Code.hasC2Code && !damageCodesStorage.c2Codes.length) {
      getC2Codes();
    }
  }, [
    damageCodesStorage,
    damageCodesStorage.selectedMainCode,
    damageCodesStorage.selectedC1Code,
    damageCodesStorage.c2Codes,
    damageCodesStorage.c2CodesAreLoading,
    damagePropertiesStorageModel,
    handleErrorInSnackbar,
  ]);

  useEffect(() => {
    async function getDamageDetails() {
      try {
        damageCodesStorage.setLoadingDamageCharacteristics();
        const c1CodeParam = !damageCodesStorage.selectedMainCode?.hasC1Codes
          ? ""
          : damageCodesStorage.selectedC1Code.code;
        await damagePropertiesStorageModel.getDamageDetailsForCodes(
          damageCodesStorage.selectedMainCode?.code ?? "",
          c1CodeParam,
          damageCodesStorage.selectedC2Code.code
        );
        damageCodesStorage.receivedDamageCharacteristics(
          damagePropertiesStorageModel.damageDetailsForCodes
        );
      } catch (error) {
        const applicationError = error as ApplicationError;
        applicationError.withErrorMessage("default-error.damage.characteristics");
        handleErrorInSnackbar(applicationError);
      }
    }
    if (
      damageCodesStorage.newDamageCharacteristicsRequired &&
      !damageCodesStorage.loadingDamageCharacteristics
    ) {
      getDamageDetails();
    }
  }, [
    damageCodesStorage,
    damageCodesStorage.selectedMainCode,
    damageCodesStorage.selectedC1Code,
    damageCodesStorage.selectedC2Code,
    damagePropertiesStorageModel,
    handleErrorInSnackbar,
  ]);

  useEffect(() => {
    return () => {
      videoPlayerService.resetLastScreenshot();
    };
  }, []);

  if (damagePropertiesStorageModel.isDataLoadingForCreate) {
    return <LoadingSpinner />;
  }

  return (
    <Form
      formikOptions={{
        initialValues: getInitialValues(),
        onSubmit: handleSubmit,
        validateOnBlur: false,
        validateOnChange: false,
        enableReinitialize: true,
      }}
      warningKey={damageIsLockedWarning?.key}
      warningContext={damageIsLockedWarning?.context}
      onCancel={goToInitialPage}
      canSubmit={!currentInspectionStorage.inspectionIsLockedBySomeoneElse}
    >
      {({ values, setFieldValue }) => {
        return (
          <>
            <DataSection labelKey="general.damage.details">
              <AddDamageImage
                uploadImageState={imageUploadState}
                initialImageUri={videoPlayerService.lastScreenshotUri}
              />
              <SubSection>
                <EditGroupCode
                  value={damageCodesStorage.selectedGroupCode}
                  groupCodes={damageCodesStorage.groupCodes}
                  onChange={(newGroupCode: DamageGroupCode) => {
                    damageCodesStorage.groupCodeSelected(newGroupCode);
                  }}
                  setFieldValue={setFieldValue}
                />
                <EditMainCode
                  labelKey={damageCodesStorage.mainCodeLabel}
                  value={damageCodesStorage.selectedMainCode}
                  mainCodes={damageCodesStorage.mainCodes}
                  onChange={(newMainCode: DamageMainCode | null) => {
                    damageCodesStorage.mainCodeSelected(newMainCode);
                  }}
                  setFieldValue={setFieldValue}
                />
                <EditC1Code
                  labelKey={damageCodesStorage.c1CodeLabel}
                  value={damageCodesStorage.selectedC1Code}
                  c1Codes={damageCodesStorage.c1Codes}
                  disabled={
                    !damageCodesStorage.c1Codes.length ||
                    !damageCodesStorage.selectedMainCode?.hasC1Codes
                  }
                  onChange={(newC1Code: DamageC1Code) => {
                    damageCodesStorage.c1CodeSelected(newC1Code);
                  }}
                  setFieldValue={setFieldValue}
                />
                <EditC2Code
                  labelKey={damageCodesStorage.c2CodeLabel}
                  value={damageCodesStorage.selectedC2Code}
                  c2Codes={getC2Codes()}
                  disabled={
                    !damageCodesStorage.selectedC1Code.hasC2Code ||
                    !damageCodesStorage.selectedC1Code.isValid ||
                    damageCodesStorage.c2CodesAreLoading
                  }
                  onChange={(newC2Code: DamageC2Code) => {
                    damageCodesStorage.c2CodeSelected(newC2Code);
                  }}
                  setFieldValue={setFieldValue}
                />
              </SubSection>
              <SubSection hideRightBorder>
                <DisplayDateCreated />
                <DisplayCreatedBy />
              </SubSection>
            </DataSection>
            <EditDynamicProperties
              labelKey={translationService.translate("general.characteristics")}
              properties={damageCodesStorage.damageCharacteristicsProperties}
              values={values.dynamicProperties}
              nestedFieldsPath="dynamicProperties"
              setFieldValue={setFieldValue}
              allFieldsDisabled={damageCodesStorage.characteristicsAreDisabled}
              loadingOverlay={isLoadingDamageCharacteristics()}
            />
          </>
        );
      }}
    </Form>
  );
});

export default CreateDamage;
